/* eslint-disable react/jsx-props-no-spreading */

import dynamic from "next/dynamic";
import { useEffect, useMemo, useState } from "react";

import BtaVotingContent, {
  type BtaVotingContentProps,
} from "@/components/molecules/confirmation-section/bta-voting-content";
import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import {
  APPLICATION,
  BRAND_STAYSURE,
  PRODUCT,
  VARIANT_FLOW,
} from "@/constants";
import { useRenewalStateContext } from "@/providers/renewal-state-provider";

const Footer = dynamic(
  () => import("@/components/organisms/footer/footer-section"),
  {
    ssr: false,
  }
);

type PageContentProps = {
  leftPanel: React.ReactNode;
  middlePanel: React.ReactNode;
  rightPanel: React.ReactNode;
  footer: React.ReactNode;
  btaVotingData?: Omit<BtaVotingContentProps, "variant">;
};

export default function LandingPageContent({
  leftPanel,
  middlePanel,
  rightPanel,
  footer,
  btaVotingData,
}: Readonly<PageContentProps>) {
  const { isLoading } = useRenewalStateContext();
  const [isFooterAlreadyLoaded, setIsFooterAlreadyLoaded] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsFooterAlreadyLoaded(Boolean(sessionStorage.getItem("footer")));
    }
  }, []);

  const shouldRenderDynamicFooter =
    ["avanti"].includes(PRODUCT) &&
    APPLICATION === "renewal" &&
    !isFooterAlreadyLoaded;

  const isAllCoverLevelVariant = useMemo(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.RENEWAL_DASHBOARD_ALL_COVER_LEVEL
      ) === VARIANT_FLOW &&
      PRODUCT === BRAND_STAYSURE,
    []
  );

  return (
    <div>
      <div
        className={`grid md:grid-cols-12 grid-cols-6 gap-spacing-m md:gap-spacing-l lg:px-0  ${
          shouldRenderDynamicFooter ? "md:pb-0" : "md:pb-[140px]"
        } lg:pb-spacing-xxl lg:mx-[72px]
      md:mx-[48px] mx-[24px] xl:mx-auto`}
      >
        {!isAllCoverLevelVariant && (
          <nav
            aria-label="Main Navigation"
            className="lg:col-span-2 hidden xl:block xl:sticky top-0 max-h-[600px]"
          >
            {leftPanel}
          </nav>
        )}
        <main
          className={`lg:col-span-7 ${
            isAllCoverLevelVariant ? "xl:col-span-8" : "xl:col-span-6"
          } col-span-full`}
        >
          {middlePanel}
          <div
            className={`${
              isLoading ? "hidden" : "block"
            } col-span-full md:block lg:hidden pt-spacing-s sm:pt-spacing-l md:mb-[36px]`}
          >
            {footer}
          </div>
        </main>
        <aside
          aria-labelledby="trustpilot-info"
          className="lg:col-span-5 xl:col-span-4 col-span-full  lg:sticky top-0 h-fit mb-spacing-m md:mb-0"
        >
          {rightPanel}
          {btaVotingData && <BtaVotingContent variant="2" {...btaVotingData} />}
        </aside>
      </div>
      {shouldRenderDynamicFooter ? (
        <>
          {/* Mobile footer */}
          <footer
            data-testid="footer"
            className="block col-span-full lg:hidden pt-4 pb-[45px] md:pt-6 md:pb-[140px] w-full [&_p]:pb-[7px] [&_p:last-child]:pb-0"
          >
            <Footer />
          </footer>

          {/* Desktop footer */}
          <div className="hidden col-span-full lg:block w-full lg:pb-[48px]">
            {footer}
            <footer
              data-testid="footer"
              className="hidden lg:block lg:pt-12 lg:pb-0 [&_p]:pb-[7px] [&_p:last-child]:pb-0"
            >
              <Footer />
            </footer>
          </div>
        </>
      ) : (
        /* Default footer */
        <div className="hidden col-span-full lg:block w-full lg:pb-[120px]">
          {footer}
        </div>
      )}
    </div>
  );
}
